/* down payment downpayment contribute */

import type { Uuid } from "spacetimedb";
import { useState } from "react";

import { Button } from "@/web/components/primitives/button";
import {
  Drawer,
  DrawerBody,
  DrawerCloseIcon,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/web/components/primitives/drawer";
import { formatCanonicalAddressShort } from "@/web/lib/addresses";
import { ErrorHandler } from "@/web/lib/errors";
import { formatUSDShort } from "@/web/lib/formatting";
import { useReducerMutation } from "@/web/lib/tanstack";
import { reducers } from "@tycho/web-bindings";
import type { Property } from "@tycho/web-bindings/types";
import { ActionCard } from "@/web/routes/_protected/smb/-dashboard/-ActionCard";
import { BudgetSlider } from "@/web/routes/_protected/smb/-dashboard/-BudgetSlider";
import { CardRulesEngine } from "@/web/routes/_protected/smb/-dashboard/-CardRulesEngine";
import type {
  SmbActionCoreProps,
  SmbActionDefinition,
} from "@/web/routes/_protected/smb/-dashboard/-types";

const CONTRIBUTION_FALLBACK_MAX = 10_000_000;

/**
 * One row in the contribution drawer. Each targeted property is in one state:
 *   - `set`: already has an intended contribution → show the value, no slider.
 *   - `ready`: a max budget is set, no contribution yet → interactive slider.
 *   - `locked`: no max budget yet → disabled slider ("set a budget first").
 */
type ContributionTarget = {
  property: Property;
  min: number;
  max: number;
  defaultValue: number;
  state: "set" | "ready" | "locked";
  setValue?: number;
};

type IntendedContributionProps = {
  underwritableEntityId: Uuid;
  /** One entry per targeted property whose intended contribution is still unset. */
  targets: ContributionTarget[];
};

function IntendedContributionAction({
  underwritableEntityId,
  targets,
  onSkip,
  onComplete,
}: IntendedContributionProps & SmbActionCoreProps) {
  const [open, setOpen] = useState(false);
  const multiple = targets.length > 1;

  return (
    <>
      <ActionCard category="fundraise">
        <ActionCard.Header>
          <ActionCard.Badge>Fundraise</ActionCard.Badge>
          <ActionCard.DetailsTrigger />
        </ActionCard.Header>
        <ActionCard.Main>
          <ActionCard.Title>
            {multiple
              ? "How much would you want to invest in each property?"
              : "How much would you want to invest?"}
          </ActionCard.Title>
          <ActionCard.Description>You can adjust these numbers later.</ActionCard.Description>
        </ActionCard.Main>
        <ActionCard.Footer>
          <ActionCard.SkipButton onSkip={onSkip} />
          <ActionCard.PrimaryButton onClick={() => setOpen(true)}>
            Set contribution{multiple ? "s" : ""}
          </ActionCard.PrimaryButton>
        </ActionCard.Footer>
        <ActionCard.Back title="What this number means">
          <div className="flex flex-col gap-2">
            <p>This is the amount you plan to invest alongside your community.</p>
            <p>
              Your contribution works like a down payment — it's paid at closing and reduces the
              total "loan" amount against the total price of the property.
            </p>
          </div>
        </ActionCard.Back>
      </ActionCard>

      <IntendedContributionDrawer
        open={open}
        underwritableEntityId={underwritableEntityId}
        targets={targets}
        onOpenChange={setOpen}
        onComplete={onComplete}
      />
    </>
  );
}

function IntendedContributionDrawer({
  open,
  underwritableEntityId,
  targets,
  onOpenChange,
  onComplete,
}: {
  open: boolean;
  underwritableEntityId: Uuid;
  targets: ContributionTarget[];
  onOpenChange: (open: boolean) => void;
  onComplete: (result?: unknown) => void;
}) {
  const [values, setValues] = useState<Record<string, number>>(() =>
    Object.fromEntries(targets.map((t) => [t.property.id.toString(), t.defaultValue])),
  );
  const { mutateAsync: setUserContribution, isPending } = useReducerMutation(
    reducers.setUserIntendedContributionForTargetableProperty,
  );

  const setValue = (id: string, next: number) => setValues((prev) => ({ ...prev, [id]: next }));

  const readyTargets = targets.filter((t) => t.state === "ready");

  const handleDone = async () => {
    try {
      for (const target of readyTargets) {
        const id = target.property.id;
        await setUserContribution({
          underwritableEntityId,
          targetablePropertyId: id,
          userIntendedContribution: {
            value: values[id.toString()] ?? target.defaultValue,
            note: undefined,
          },
        });
      }
      onOpenChange(false);
      onComplete({ contributions: values });
    } catch (error) {
      ErrorHandler.default(error);
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>
            How much would you invest{targets.length > 1 ? " in each" : ""}?
          </DrawerTitle>
          <DrawerCloseIcon />
        </DrawerHeader>
        <DrawerBody className="flex flex-col gap-4 py-6">
          {targets.map((target) => {
            const id = target.property.id.toString();
            return (
              <section key={id} className="flex flex-col gap-1 border border-border px-4 pt-3 pb-1">
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="font-heading text-base uppercase">
                    {formatCanonicalAddressShort(target.property.address)}
                  </h3>
                  {target.state === "set" ? (
                    <span className="withus-strong text-base text-withus-blue">
                      {target.setValue !== undefined ? formatUSDShort(target.setValue) : "Set"}
                    </span>
                  ) : target.state === "locked" ? (
                    <span className="text-xs text-muted-foreground">Set a budget first</span>
                  ) : null}
                </div>
                {target.state === "set" ? (
                  <p className="pb-2 text-xs text-muted-foreground">
                    You plan to invest this amount alongside your community.
                  </p>
                ) : (
                  <BudgetSlider
                    value={values[id] ?? target.defaultValue}
                    onChange={(next) => setValue(id, next)}
                    min={target.min}
                    max={target.max}
                    category="fundraise"
                    tone="on-surface"
                    disabled={target.state === "locked"}
                  />
                )}
              </section>
            );
          })}
        </DrawerBody>
        <DrawerFooter>
          <Button
            color="red"
            size="lg"
            isLoading={isPending}
            disabled={isPending || readyTargets.length === 0}
            onClick={handleDone}
          >
            Done
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

export function createIntendedContributionAction(
  props: IntendedContributionProps & { id?: string; canSkip?: boolean },
): SmbActionDefinition {
  const { id = "intended-contribution", canSkip = true, ...rest } = props;
  return {
    id,
    category: "fundraise",
    canSkip,
    render: (core) => <IntendedContributionAction {...rest} {...core} />,
  };
}

/** A property is awaiting a contribution once its max budget is set and none is. */
const propertyAwaitingContribution = (p: Property) =>
  p.userMaxPrice !== undefined && p.userIntendedContribution === undefined;

const contributionTargetFor = (p: Property): ContributionTarget => {
  const max = p.userMaxPrice?.value ?? CONTRIBUTION_FALLBACK_MAX;
  const state: ContributionTarget["state"] =
    p.userIntendedContribution !== undefined
      ? "set"
      : p.userMaxPrice !== undefined
        ? "ready"
        : "locked";
  return {
    property: p,
    min: 0,
    max,
    defaultValue: max / 5,
    state,
    setValue: p.userIntendedContribution?.value,
  };
};

// Single card across every targeted property: shows when ANY property with a
// max budget still has no intended contribution. The drawer lists every
// targeted property — already-set ones show their value, budget-set ones get an
// interactive slider, and properties without a budget yet a disabled one ("set
// a budget first"). Leaves the deck once every budgeted property has a
// contribution.
export const intendedContributionRule = CardRulesEngine.singletonRule({
  id: "intended-contribution",
  priority: 60,
  when: (d) => d.properties.some(propertyAwaitingContribution),
  build: (d) =>
    createIntendedContributionAction({
      underwritableEntityId: d.underwritableEntityId,
      targets: d.properties.map(contributionTargetFor),
    }),
});
